import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { ArrowsRightLeftIcon, BanknotesIcon, HomeModernIcon, NoSymbolIcon, UserMinusIcon, WrenchScrewdriverIcon } from '@heroicons/react/24/outline';
import SwitchCard from '@ui/switchcard/SwitchCard';
import FormSelect from '@ui/form/input/FormSelect';
import FormButton from '@ui/button/FormButton';
import Message from '@ui/message/Message';
import useToaster from '@app/component/overlays/toaster/ToasterHook';
import { Integration, IntegrationProvider, IntegrationReportRules, IntegrationRoomRevenue } from '@app/model/integration-settings/Integration';
import { DEFAULT_REPORT_RULES, ruleCaptions } from '@app/model/integration-settings/ReportRules';
import { useSaveIntegrationReportRules } from '@app/model/integration-settings/service/IntegrationSettingsHooks';
import { PROVIDER_LABEL } from '../IntegrationSettings.utilities';

const ROOM_REVENUE_OPTIONS: { id: IntegrationRoomRevenue, label: string }[] = [
	{ id: 'STAY_NIGHTS', label: 'Stay nights' },
	{ id: 'ALL_ROOM_POSTINGS', label: 'All room postings (as the PMS report)' },
];

// The lines of the tab that name what the provider sends (ROO2-316 Q5). A provider without its own lines takes the
// lines of OPERA with its name.
type RuleTexts = { outOfOrder: string, noShow: string, houseUse: string, allRoomPostings: string };
const ruleTexts = (provider: IntegrationProvider | null): RuleTexts => {
	if (provider === 'HOTELIZER') return {
		outOfOrder: 'On: occupancy and RevPAR divide by the rooms minus the rooms that Hotelizer marks out of order on the night, from its daily statistics. Off: by all rooms.',
		noShow: 'A booked room whose guest did not come (Hotelizer status No Show) counts as occupied on its arrival day.',
		houseUse: 'Hotelizer marks no rate plan as house use, so this switch changes no figure of this property. Complimentary nights always count.',
		allRoomPostings: 'Also the room revenue that Hotelizer posts outside the counted nights: day use, no-shows, cancellation charges, virtual rooms, room types that are no longer active and charges outside the stay. Room nights do not change.',
	};
	const name = provider === null || provider === 'OPERA' ? 'OPERA' : PROVIDER_LABEL[provider];
	return {
		outOfOrder: `On: occupancy and RevPAR divide by the rooms minus the rooms that ${name} marks out of order on the night. Off: by all rooms.`,
		noShow: `A booked room whose guest did not come counts as occupied on its arrival day, also when ${name} moved the arrival one night later.`,
		houseUse: `A night on a rate plan that ${name} marks as house use stays a room night. Off: it leaves the occupied rooms, as in most PMS reports. Complimentary nights always count.`,
		allRoomPostings: `Also the room revenue that ${name} posts outside the counted nights: day use, no-shows, cancellations, pseudo rooms and postings outside the stay. Room nights do not change.`,
	};
};

const roomRevenueHelp = (roomRevenue: IntegrationRoomRevenue, texts: RuleTexts): string =>
	roomRevenue === 'STAY_NIGHTS' ? 'The revenue of the counted room nights only.' : texts.allRoomPostings;

// ROO2-315 B1 + ROO2-309 Q1 (decision Q1 of part b): the third tab of a Reservations card whose provider has report rules
// (OPERA, Hotelizer). Four switches and the Room revenue select decide how the BI reports count occupancy, ADR and RevPAR,
// so that they can match the hotel's PMS report. The tab has its own Save. The preview under the switches uses the same
// words as the BI captions (315 Q5).
const ReportRulesTab: React.FunctionComponent<{ integration: Integration }> = ({ integration }) => {
	const toaster = useToaster();
	const saved = integration.reportRules ?? DEFAULT_REPORT_RULES;
	const [rules, setRules] = useState<IntegrationReportRules>(saved);
	const form = useForm<{ roomRevenue: IntegrationRoomRevenue }>({ defaultValues: { roomRevenue: saved.roomRevenue } });
	const roomRevenue = form.watch('roomRevenue');
	useEffect(() => setRules(r => ({ ...r, roomRevenue })), [roomRevenue]);
	const save = useSaveIntegrationReportRules(
		next => { setRules(next.reportRules ?? DEFAULT_REPORT_RULES); toaster.success('Report rules', 'RoomPulse saved the report rules. The reports use them from their next load.'); },
		error => toaster.error('Report rules', error));
	const isDirty = JSON.stringify(rules) !== JSON.stringify(saved);
	const set = (key: keyof Omit<IntegrationReportRules, 'roomRevenue'>) => (value: boolean) => setRules({ ...rules, [key]: value });
	const captions = ruleCaptions(rules, integration.catalogue.rooms ?? null);
	const texts = ruleTexts(integration.provider);

	return <div className="flex flex-col gap-6">
		<p className="text-sm text-gray-500">
			These rules decide how the BI reports count occupancy, ADR and RevPAR for this property, so that they can match the
			PMS report. RoomPulse sets them from a check of the property's report; change them only when the PMS report changes.
			Daily Performance uses them now; the other reports follow.
		</p>

		<div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
			<SwitchCard icon={<WrenchScrewdriverIcon className="h-5 w-5" />} ariaLabel="Out-of-order rooms reduce the available rooms"
				title="Out-of-order rooms reduce the available rooms"
				description={texts.outOfOrder}
				checked={rules.outOfOrderReducesRooms} onChange={set('outOfOrderReducesRooms')} disabled={save.isPending} muted={!rules.outOfOrderReducesRooms} />
			<SwitchCard icon={<ArrowsRightLeftIcon className="h-5 w-5" />} ariaLabel="Day-use rooms count as occupied"
				title="Day-use rooms count as occupied"
				description="A room that arrives and leaves on the same day counts as an occupied room on that day. It is never a room night."
				checked={rules.dayUseOccupied} onChange={set('dayUseOccupied')} disabled={save.isPending} muted={!rules.dayUseOccupied} />
			<SwitchCard icon={<UserMinusIcon className="h-5 w-5" />} ariaLabel="No-show rooms count as occupied"
				title="No-show rooms count as occupied"
				description={texts.noShow}
				checked={rules.noShowOccupied} onChange={set('noShowOccupied')} disabled={save.isPending} muted={!rules.noShowOccupied} />
			<SwitchCard icon={<HomeModernIcon className="h-5 w-5" />} ariaLabel="House-use rooms count as occupied"
				title="House-use rooms count as occupied"
				description={texts.houseUse}
				checked={rules.houseUseOccupied} onChange={set('houseUseOccupied')} disabled={save.isPending} muted={!rules.houseUseOccupied} />
		</div>

		<div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
			<div>
				<FormSelect form={form} name="roomRevenue" label="Room revenue" disabled={save.isPending}
					onValueChange={value => form.setValue('roomRevenue', value as IntegrationRoomRevenue, { shouldDirty: true })}
					options={ROOM_REVENUE_OPTIONS.map(o => ({ id: o.id, label: o.label, value: o.id }))} />
				<p className="help">{roomRevenueHelp(rules.roomRevenue, texts)}</p>
			</div>
			<div className="sm:col-span-2">
				<Message state="info" title="The reports will count">
					<span className="flex items-start gap-x-2"><BanknotesIcon className="mt-0.5 h-4 w-4 flex-none" /><span>
						<strong>Occupancy</strong> = {captions.occupancy}<br />
						<strong>ADR</strong> = {captions.adr}<br />
						<strong>RevPAR</strong> = {captions.revpar}
					</span></span>
				</Message>
			</div>
		</div>

		<div className="flex flex-col gap-4 border-t border-gray-200 pt-4 sm:flex-row sm:items-center sm:justify-between">
			<p className="flex items-center gap-x-1 text-sm text-gray-500"><NoSymbolIcon className="h-4 w-4" />
				No re-import: a change shows at the next load of a report.</p>
			<div className="flex flex-wrap items-center gap-3">
				{isDirty && <FormButton state="white" onClick={() => { setRules(saved); form.reset({ roomRevenue: saved.roomRevenue }); }} disabled={save.isPending}>Cancel</FormButton>}
				{isDirty && <FormButton state="primary" onClick={() => save.mutate({ integrationId: integration.id, rules })} disabled={save.isPending}>Save</FormButton>}
			</div>
		</div>
	</div>;
};

export default ReportRulesTab;
